import { RouteName } from '@/router/types';

/**
 * Query marker appended to the Firebase password-reset "continue" URL.
 *
 * Firebase adds it to the link behind the "Continue" button on its hosted
 * reset page. Arriving with it only means the user followed that link — it is
 * NOT proof that a new password was saved, so the login page words its hint
 * conditionally.
 */
export const PASSWORD_RESET_RETURN_QUERY_KEY = 'from';
export const PASSWORD_RESET_RETURN_QUERY_VALUE = 'password-reset';

export const PASSWORD_RESET_RESEND_COOLDOWN_SECONDS = 60;

/**
 * Errors that mean "the continue URL was rejected" (e.g. the domain is not in
 * Firebase Auth's authorized domains). The reset email can still be sent
 * without a continue URL, so these must not block account recovery.
 */
const CONTINUE_URL_ERROR_CODES = new Set([
  'auth/unauthorized-continue-uri',
  'auth/invalid-continue-uri',
  'auth/missing-continue-uri',
]);

export const buildPasswordResetContinueUrl = (appWebBaseUrl: string): string =>
  `${appWebBaseUrl.replace(/\/$/, '')}/${RouteName.Login}?${PASSWORD_RESET_RETURN_QUERY_KEY}=${PASSWORD_RESET_RETURN_QUERY_VALUE}`;

export const isPasswordResetReturn = (query: Record<string, unknown> | undefined): boolean => {
  const raw = query?.[PASSWORD_RESET_RETURN_QUERY_KEY];
  const value = Array.isArray(raw) ? raw[0] : raw;
  return value === PASSWORD_RESET_RETURN_QUERY_VALUE;
};

const errorCode = (error: unknown): string =>
  error && typeof error === 'object' && 'code' in error && typeof error.code === 'string' ? error.code : '';

/**
 * With email enumeration protection disabled, Firebase rejects unknown
 * addresses. We answer exactly as for a registered address so the screen never
 * reveals whether an account exists.
 */
export const isAccountNotFoundError = (error: unknown): boolean => errorCode(error) === 'auth/user-not-found';

export const isContinueUrlError = (error: unknown): boolean => CONTINUE_URL_ERROR_CODES.has(errorCode(error));

export const isTooManyRequestsError = (error: unknown): boolean => errorCode(error) === 'auth/too-many-requests';

// Hands the email typed on the login form to the reset screen in memory, so it
// never ends up in a URL (and therefore never in RUM view URLs or history).
let prefillEmail = '';

export const setPasswordResetPrefillEmail = (email: string | undefined | null) => {
  prefillEmail = typeof email === 'string' ? email.trim() : '';
};

export const consumePasswordResetPrefillEmail = (): string => {
  const email = prefillEmail;
  prefillEmail = '';
  return email;
};
